import React, { useEffect, useState } from 'react';
import { X, Sparkle, HouseLine, CaretRight, Heart, Check, Info } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';
export interface MovedInOfferOption {
  id: string;
  counterpartyName: string;
  counterpartyAvatar?: string;
  listingTitle?: string;
  listingPrice?: string;
}
export interface MovedInPreselected {
  id: string;
  counterpartyName: string;
  counterpartyAvatar?: string;
  listingTitle?: string;
  listingImage?: string;
  listingPrice?: string;
  listingLocation?: string;
}
interface MovedInModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'from-offer' | 'from-toggle';
  preselectedOffer?: MovedInPreselected;
  availableOffers?: MovedInOfferOption[];
  onConfirmIzioMatch: (offerId: string) => void;
  onConfirmExternal: () => void;
  onPause: () => void;
  onRequestExternalReview: () => void;
}
type Step = 'confirm' | 'frame' | 'picker' | 'external' | 'reputation';
export function MovedInModal({
  isOpen,
  onClose,
  mode,
  preselectedOffer,
  availableOffers = [],
  onConfirmIzioMatch,
  onConfirmExternal,
  onPause,
  onRequestExternalReview
}: MovedInModalProps) {
  const [step, setStep] = useState<Step>(mode === 'from-offer' ? 'confirm' : 'frame');
  const [pickedOfferId, setPickedOfferId] = useState<string | null>(null);
  useEffect(() => {
    if (isOpen) {
      setStep(mode === 'from-offer' ? 'confirm' : 'frame');
      setPickedOfferId(null);
    }
  }, [isOpen, mode]);
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const handleConfirmFromOffer = () => {
    if (!preselectedOffer) return;
    setStep('reputation');
  };
  const handlePickContinue = () => {
    if (!pickedOfferId) return;
    setStep('reputation');
  };
  const handleReputationDone = () => {
    if (mode === 'from-offer' && preselectedOffer) {
      onConfirmIzioMatch(preselectedOffer.id);
    } else if (pickedOfferId) {
      onConfirmIzioMatch(pickedOfferId);
    }
  };
  return <div role="dialog" aria-modal="true" aria-label="Te-ai mutat?" className="fixed inset-0 z-[100] bg-dark-1/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 lg:pl-[16rem] animate-in fade-in duration-200" onClick={onClose} data-id="element-1397">
      <div className="w-full sm:max-w-lg bg-dark-2 sm:rounded-2xl rounded-t-2xl border-t sm:border border-dark-3 max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-200" onClick={e => e.stopPropagation()} data-id="element-1398">
        {/* Header */}
        <header className="flex items-center justify-between px-5 py-4 border-b border-dark-3 shrink-0" data-id="element-1399">
          <h2 className="text-base font-semibold text-light-1" data-id="element-1400">
            {step === 'external' ? 'Felicitări pentru noul tău acasă' : step === 'reputation' ? 'Contractul tău e activ' : 'Te-ai mutat?'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Închide" className="w-9 h-9 inline-flex items-center justify-center rounded-md text-light-3 hover:text-light-1 hover:bg-dark-3 transition-colors cursor-pointer" data-id="element-1401">
            <X size={18} weight="bold" data-id="element-1402" />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-6" data-id="element-1403">
          {/* STEP: confirm (from-offer) */}
          {step === 'confirm' && preselectedOffer && <div className="space-y-5" data-id="element-1404">
              <div className="flex items-start gap-3" data-id="element-1405">
                <div className="w-10 h-10 rounded-2xl bg-lime-5 border border-lime-4 inline-flex items-center justify-center shrink-0" data-id="element-1406">
                  <Sparkle size={18} weight="fill" className="text-lime-1" data-id="element-1407" />
                </div>
                <div className="min-w-0" data-id="element-1408">
                  <h3 className="text-lg font-semibold text-light-1 leading-tight" data-id="element-1409">
                    Te-ai mutat? Felicitări! 🎉
                  </h3>
                  <p className="text-sm text-light-3 mt-1.5 leading-relaxed" data-id="element-1410">
                    Confirmi că ai închiriat apartamentul de la{' '}
                    <span className="font-medium text-light-1" data-id="element-1411">
                      {preselectedOffer.counterpartyName}
                    </span>
                    ?
                  </p>
                </div>
              </div>

              {/* Listing snippet */}
              <div className="rounded-xl bg-dark-3 border border-dark-4 p-3 flex items-center gap-3" data-id="element-1412">
                {preselectedOffer.listingImage ? <img src={preselectedOffer.listingImage} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" data-id="element-1413" /> : <div className="w-14 h-14 rounded-lg bg-dark-4 inline-flex items-center justify-center shrink-0" data-id="element-1414">
                    <HouseLine size={20} className="text-light-3" data-id="element-1415" />
                  </div>}
                <div className="min-w-0 flex-1" data-id="element-1416">
                  {preselectedOffer.listingTitle && <p className="text-sm font-medium text-light-1 truncate" data-id="element-1417">
                      {preselectedOffer.listingTitle}
                    </p>}
                  <p className="text-xs text-light-3 mt-0.5 truncate" data-id="element-1418">
                    {preselectedOffer.listingPrice}
                    {preselectedOffer.listingLocation && <>
                        <span className="text-light-4" data-id="element-1419"> · </span>
                        {preselectedOffer.listingLocation}
                      </>}
                  </p>
                </div>
              </div>
            </div>}

          {/* STEP: frame (from-toggle) */}
          {step === 'frame' && <div className="space-y-5" data-id="element-1420">
              <div className="flex items-start gap-3" data-id="element-1421">
                <div className="w-10 h-10 rounded-2xl bg-lime-5 border border-lime-4 inline-flex items-center justify-center shrink-0" data-id="element-1422">
                  <Sparkle size={18} weight="fill" className="text-lime-1" data-id="element-1423" />
                </div>
                <div className="min-w-0" data-id="element-1424">
                  <h3 className="text-lg font-semibold text-light-1 leading-tight" data-id="element-1425">
                    Te-ai mutat? 🎉
                  </h3>
                  <p className="text-sm text-light-3 mt-1.5 leading-relaxed" data-id="element-1426">
                    Înainte să închidem anunțul — ai închiriat printr-o ofertă
                    din Izio?
                  </p>
                </div>
              </div>

              <div className="space-y-2.5 pt-2" data-id="element-1427">
                <button type="button" onClick={() => setStep('picker')} disabled={availableOffers.length === 0} className={cn('w-full h-12 rounded-md inline-flex items-center justify-center gap-2 text-sm font-semibold transition-colors', availableOffers.length === 0 ? 'bg-dark-3 text-light-4 cursor-not-allowed' : 'bg-lime-1 hover:bg-lime-2 text-dark-1 cursor-pointer')} data-id="element-1428">
                  Da, prin Izio
                </button>
                <button type="button" onClick={() => setStep('external')} className="w-full h-12 rounded-md inline-flex items-center justify-center gap-2 text-sm font-medium bg-dark-3 hover:bg-dark-4 border border-dark-4 text-light-1 transition-colors cursor-pointer" data-id="element-1429">
                  Da, dar din altă parte
                </button>
                <button type="button" onClick={onPause} className="w-full h-10 inline-flex items-center justify-center text-xs text-light-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-1430">
                  Doar îmi iau o pauză
                </button>
              </div>
            </div>}

          {/* STEP: picker (from-toggle → Da, prin Izio) */}
          {step === 'picker' && <div className="space-y-4" data-id="element-1431">
              <div data-id="element-1432">
                <h3 className="text-lg font-semibold text-light-1" data-id="element-1433">
                  Cu cine ai închiriat?
                </h3>
                <p className="text-sm text-light-3 mt-1" data-id="element-1434">
                  Alege oferta — ne ajută să-ți construim reputația.
                </p>
              </div>
              <ul className="space-y-2 max-h-[320px] overflow-y-auto pr-1" data-id="element-1435">
                {availableOffers.slice(0, 10).map(o => {
              const picked = pickedOfferId === o.id;
              return <li key={o.id} data-id="element-1436">
                      <button type="button" onClick={() => setPickedOfferId(o.id)} className={cn('w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left cursor-pointer', picked ? 'bg-lime-5 border-lime-4' : 'bg-dark-3 border-dark-4 hover:border-light-4')} data-id="element-1437">
                        {o.counterpartyAvatar ? <img src={o.counterpartyAvatar} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" data-id="element-1438" /> : <div className="w-9 h-9 rounded-full bg-dark-4 inline-flex items-center justify-center text-sm font-medium text-light-2 shrink-0" data-id="element-1439">
                            {o.counterpartyName.charAt(0)}
                          </div>}
                        <div className="min-w-0 flex-1" data-id="element-1440">
                          <p className="text-sm font-medium text-light-1 truncate" data-id="element-1441">
                            {o.counterpartyName}
                          </p>
                          {o.listingTitle && <p className="text-xs text-light-3 truncate mt-0.5" data-id="element-1442">
                              {o.listingTitle}
                              {o.listingPrice && <>
                                  <span className="text-light-4" data-id="element-1443"> · </span>
                                  {o.listingPrice}
                                </>}
                            </p>}
                        </div>
                        {picked && <div className="w-5 h-5 rounded-full bg-lime-1 inline-flex items-center justify-center shrink-0" data-id="element-1444">
                            <Check size={12} weight="bold" className="text-dark-1" data-id="element-1445" />
                          </div>}
                      </button>
                    </li>;
            })}
              </ul>
            </div>}

          {/* STEP: external (from-toggle → Da, din altă parte) */}
          {step === 'external' && <div className="space-y-5" data-id="element-1446">
              <div className="flex items-start gap-3" data-id="element-1447">
                <div className="w-10 h-10 rounded-2xl bg-lime-5 border border-lime-4 inline-flex items-center justify-center shrink-0" data-id="element-1448">
                  <HouseLine size={20} weight="fill" className="text-lime-1" data-id="element-1449" />
                </div>
                <div className="min-w-0" data-id="element-1450">
                  <h3 className="text-lg font-semibold text-light-1 leading-tight" data-id="element-1451">
                    Ne bucurăm că ți-ai găsit locul potrivit! 🏡
                  </h3>
                  <p className="text-sm text-light-3 mt-1.5 leading-relaxed" data-id="element-1452">
                    Indiferent unde s-a întâmplat, succesul tău e și succesul
                    nostru. Ne vedem când va fi cazul.
                  </p>
                </div>
              </div>

              <button type="button" onClick={onRequestExternalReview} className="w-full h-11 rounded-md inline-flex items-center justify-center gap-2 bg-dark-2 hover:bg-dark-3 border border-lime-4 text-lime-1 text-sm font-medium transition-colors cursor-pointer" data-id="element-1453">
                <Heart size={16} weight="fill" data-id="element-1454" />
                Cere un review de la proprietar
              </button>

              <p className="text-xs text-light-4 leading-relaxed" data-id="element-1455">
                Peste un an, îți reamintim să-ți construiești reputația — chiar
                și pentru închirierile din afara Izio.
              </p>
            </div>}

          {/* STEP: reputation (shared) */}
          {step === 'reputation' && <div className="space-y-5" data-id="element-1456">
              <div className="flex items-start gap-3" data-id="element-1457">
                <div className="w-10 h-10 rounded-2xl bg-lime-5 border border-lime-4 inline-flex items-center justify-center shrink-0" data-id="element-1458">
                  <Sparkle size={20} weight="fill" className="text-lime-1" data-id="element-1459" />
                </div>
                <div className="min-w-0" data-id="element-1460">
                  <h3 className="text-lg font-semibold text-light-1 leading-tight" data-id="element-1461">
                    Excelent! Contractul tău e activ.
                  </h3>
                  <p className="text-sm text-light-3 mt-1.5 leading-relaxed" data-id="element-1462">
                    Peste un an, vă vom invita reciproc să lăsați un review.
                    Asta te va ajuta să închiriezi și mai ușor data viitoare.
                  </p>
                </div>
              </div>

              <div className="rounded-xl bg-dark-3 border border-dark-4 p-3 flex items-start gap-2.5" data-id="element-1463">
                <Info size={16} weight="fill" className="text-light-3 shrink-0 mt-0.5" data-id="element-1464" />
                <p className="text-xs text-light-2 leading-relaxed" data-id="element-1465">
                  Anunțul tău se închide automat acum. Restul ofertelor vor fi
                  arhivate.
                </p>
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-dark-3 shrink-0 space-y-2" data-id="element-1466">
          {step === 'confirm' && <div className="flex gap-2" data-id="element-1467">
              <button type="button" onClick={onClose} className="flex-1 h-11 rounded-md bg-dark-3 hover:bg-dark-4 border border-dark-4 text-light-1 text-sm font-medium transition-colors cursor-pointer" data-id="element-1468">
                Anulează
              </button>
              <button type="button" onClick={handleConfirmFromOffer} className="flex-1 h-11 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 text-sm font-semibold transition-colors cursor-pointer" data-id="element-1469">
                Da, am închiriat
              </button>
            </div>}

          {step === 'picker' && <button type="button" onClick={handlePickContinue} disabled={!pickedOfferId} className={cn('w-full h-11 rounded-md inline-flex items-center justify-center gap-2 text-sm font-semibold transition-colors', pickedOfferId ? 'bg-lime-1 hover:bg-lime-2 text-dark-1 cursor-pointer' : 'bg-dark-3 text-light-4 cursor-not-allowed')} data-id="element-1470">
              Continuă
              <CaretRight size={14} weight="bold" data-id="element-1471" />
            </button>}

          {step === 'external' && <button type="button" onClick={onConfirmExternal} className="w-full h-11 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 text-sm font-semibold transition-colors cursor-pointer" data-id="element-1472">
              Închide anunțul
            </button>}

          {step === 'reputation' && <button type="button" onClick={handleReputationDone} className="w-full h-11 rounded-md bg-lime-1 hover:bg-lime-2 text-dark-1 text-sm font-semibold transition-colors cursor-pointer" data-id="element-1473">
              Gata, închide
            </button>}
        </div>
      </div>
    </div>;
}